function timeToMinutes(value) {
  const [hours, minutes] = value.split(':').map(Number)
  return hours * 60 + minutes
}

function minutesToTime(value) {
  const normalized = Math.max(0, Math.min(1439, value))
  return `${String(Math.floor(normalized / 60)).padStart(2, '0')}:${String(normalized % 60).padStart(2, '0')}`
}

function roundToFive(value) {
  return Math.round(value / 5) * 5
}

const platformPeakWindows = {
  xiaohongshu: [['07:30', '09:30', 2.1], ['11:30', '13:30', 2.2], ['18:00', '22:30', 3]],
  douyin: [['11:30', '14:00', 2.1], ['17:30', '23:00', 3]],
  wechat: [['07:30', '09:30', 2], ['11:30', '13:30', 2.1], ['18:00', '22:00', 2.8]],
  kuaishou: [['11:30', '13:30', 2], ['18:00', '23:00', 2.8]],
}

function getBoundaryBuffer(duration) {
  if (duration <= 60) return 5
  return Math.min(45, Math.max(20, roundToFive(duration * 0.04)))
}

function getPeakWeight(minute, platform) {
  const window = (platformPeakWindows[platform] || []).find(([start, end]) => minute >= timeToMinutes(start) && minute <= timeToMinutes(end))
  return window?.[2] || 1
}

function enforceMinimumSpacing(values, start, end) {
  if (values.length < 2) return values.map(roundToFive)
  const maximumGap = Math.max(5, Math.floor((end - start) / (values.length - 1) / 5) * 5)
  const averageSlot = (end - start) / values.length
  const minimumGap = Math.min(maximumGap, Math.max(10, Math.floor(averageSlot * 0.55 / 5) * 5))
  const result = []
  values.forEach((value, index) => {
    const minimum = index ? result[index - 1] + minimumGap : start
    const maximum = end - (values.length - 1 - index) * minimumGap
    result.push(Math.max(minimum, Math.min(maximum, roundToFive(value))))
  })
  return result
}

function distributeUniformly(start, end, count) {
  return Array.from({ length: count }, (_, index) => start + (end - start) * (index + 0.5) / count)
}

function distributeByPlatformPeaks(start, end, count, platform) {
  const candidates = []
  for (let minute = Math.ceil(start / 5) * 5; minute <= Math.floor(end / 5) * 5; minute += 5) candidates.push({ minute, weight: getPeakWeight(minute, platform) })
  const totalWeight = candidates.reduce((sum, item) => sum + item.weight, 0)
  return Array.from({ length: count }, (_, index) => {
    const targetWeight = totalWeight * (index + 0.5) / count
    let accumulated = 0
    const candidate = candidates.find(item => {
      accumulated += item.weight
      return accumulated >= targetWeight
    })
    return candidate?.minute ?? start + (end - start) * (index + 0.5) / count
  })
}

export function distributeTimes(startTime, endTime, count, options = {}) {
  if (!count) return []
  const start = timeToMinutes(startTime)
  const end = timeToMinutes(endTime)
  if (end <= start) return [minutesToTime(start)]
  const buffer = getBoundaryBuffer(end - start)
  const effectiveStart = Math.min(end, start + buffer)
  const effectiveEnd = Math.max(effectiveStart, end - buffer)
  const values = options.mode === 'uniform'
    ? distributeUniformly(effectiveStart, effectiveEnd, count)
    : distributeByPlatformPeaks(effectiveStart, effectiveEnd, count, options.platform)
  return enforceMinimumSpacing(values, effectiveStart, effectiveEnd).map(minutesToTime)
}

export function getTimeDistributionMetrics(times) {
  const values = times.map(timeToMinutes)
  const gaps = values.slice(1).map((value, index) => value - values[index])
  return {
    averageGap: gaps.length ? Math.round(gaps.reduce((sum, gap) => sum + gap, 0) / gaps.length) : null,
    shortestGap: gaps.length ? Math.min(...gaps) : null,
  }
}

export function getTimePosition(time, startTime, endTime) {
  const start = timeToMinutes(startTime)
  const end = timeToMinutes(endTime)
  if (end <= start) return 0
  return Math.max(0, Math.min(100, (timeToMinutes(time) - start) / (end - start) * 100))
}

export function buildRuleTasks(rules) {
  const activeRules = rules.filter(rule => Number(rule.dailyCount) > 0)
  const maximumCount = Math.max(0, ...activeRules.map(rule => Number(rule.dailyCount)))
  return Array.from({ length: maximumCount }, (_, index) => activeRules.filter(rule => Number(rule.dailyCount) > index)).flat()
}
